import i18n from '@/plugins/i18n';
import { script } from '@/assets/util/append-file';

// @ts-expect-error legacy notification module is not typed yet
import notify from '@/components/base-components/components/notification/notify';

interface GoogleFilePickerConfig {
    clientKey?: string;
}

interface GoogleFile {
    id: string;
    title: string;
    fileSize: number | string;
    fileExtension: string;
}

interface GoogleAuthToken {
    access_token: string;
}

interface GooglePickerError {
    error?: string;
    details?: string;
}

type SelectCallback = (file: GoogleFile, authToken?: GoogleAuthToken) => void;
type EventCallback = (error?: string) => void;

interface GapiAuthInstance {
    signIn(): Promise<void>;
    then(onFulfilled: () => void, onRejected: (response: GooglePickerError) => void): void;
}

interface GapiRequest {
    execute(callback: (file: GoogleFile) => void): void;
}

interface Gapi {
    auth: {
        getToken(): GoogleAuthToken | undefined;
    };
    auth2: {
        init(options: { client_id: string; scope: string }): void;
        getAuthInstance(): GapiAuthInstance;
    };
    client: {
        drive: {
            files: {
                get(options: { fileId: unknown }): GapiRequest;
            };
        };
        load(api: string, version: string): void;
    };
    load(api: string, version: string): void;
}

interface GooglePickerBuilder {
    addView(viewId: string): GooglePickerBuilder;
    setAppId(appId: string): GooglePickerBuilder;
    setOAuthToken(token: string): GooglePickerBuilder;
    setCallback(callback: (data: Record<string, unknown>) => void): GooglePickerBuilder;
    build(): {
        setVisible(visible: boolean): void;
    };
}

interface GooglePicker {
    Response: {
        ACTION: string;
        DOCUMENTS: string;
    };
    Action: {
        PICKED: string;
        CANCEL: string;
    };
    Document: {
        ID: string;
    };
    ViewId: {
        DOCS: string;
    };
    PickerBuilder: new () => GooglePickerBuilder;
}

declare const gapi: Gapi;
declare const google: {
    picker: GooglePicker;
};

let clientKey: string | undefined;
let onSelectCallback: SelectCallback | undefined;
let onCloseCallback: EventCallback | undefined;
let onShowCallback: EventCallback | undefined;

function fileGetCallback(file: GoogleFile) {
    if (!onSelectCallback) return;

    onSelectCallback(file, gapi.auth.getToken());
}

function pickerCallback(data: Record<string, unknown>) {
    if (data[google.picker.Response.ACTION] === google.picker.Action.PICKED) {
        const documents = data[google.picker.Response.DOCUMENTS] as Array<Record<string, unknown>>;
        const request = gapi.client.drive.files.get({
            fileId: documents[0][google.picker.Document.ID],
        });

        request.execute(fileGetCallback);
        return;
    }

    if (data[google.picker.Response.ACTION] === google.picker.Action.CANCEL) {
        onCloseCallback?.();
    }
}

function showPicker() {
    const authToken = gapi.auth.getToken();

    if (!authToken) return;

    new google.picker.PickerBuilder()
        .addView(google.picker.ViewId.DOCS)
        .setAppId(clientKey ?? '')
        .setOAuthToken(authToken.access_token)
        .setCallback(pickerCallback)
        .build()
        .setVisible(true);

    onShowCallback?.();
}

function signIn() {
    gapi.auth2.getAuthInstance().signIn().then(showPicker).catch(errorHandle);
}

function errorHandle(response: GooglePickerError) {
    if (response.error === 'idpiframe_initialization_failed') {
        notify.error({
            title: i18n.t('request error notification'),
            message: response.details,
            duration: 7500,
        });
    }

    return onCloseCallback?.(response.details);
}

export const googleFilePicker = {
    open(onSelect: SelectCallback, onClose?: EventCallback, onShow?: EventCallback) {
        if (!clientKey) {
            onClose?.();
            return;
        }

        onSelectCallback = onSelect;
        onCloseCallback = onClose;
        onShowCallback = onShow;

        const authOptions: Record<string, string> = {
            scope: 'https://www.googleapis.com/auth/drive.readonly',
        };

        // eslint-disable-next-line camelcase
        authOptions.client_id = `${clientKey}.apps.googleusercontent.com`;

        gapi.auth2.init(authOptions as { client_id: string; scope: string });

        gapi.client.load('drive', 'v2');
        gapi.load('picker', '1');

        gapi.auth2.getAuthInstance().then(signIn, errorHandle);
    },
};

export function init(config: GoogleFilePickerConfig = {}) {
    if (!config.clientKey) return;

    clientKey = config.clientKey;

    script('https://apis.google.com/js/client.js');
}
